import { AbstractControl, ValidationErrors, AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged, switchMap, startWith, take } from 'rxjs/operators';
import { PromptModuleApiService } from '@vendasta/ai-assistants';

export function urlValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return null;
    }
    return { invalidHttpUrl: true };
  } catch {
    return { invalidHttpUrl: true };
  }
}

// Create async validator for template validation
export function createTemplateValidator(promptModuleApiService: PromptModuleApiService): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const content = control.value;

    // Don't validate empty content
    if (!content || content.trim() === '') {
      return of(null);
    }

    // Only validate if the user has interacted with this field
    // This prevents validation during initial form construction
    if (!control.touched && !control.dirty) {
      return of(null);
    }

    // Use the control's valueChanges to get proper debouncing across keystrokes
    return control.valueChanges.pipe(
      startWith(content), // Include the current value
      debounceTime(500), // Debounce to avoid excessive API calls
      distinctUntilChanged(),
      switchMap((value) => {
        // Skip validation for empty content
        if (!value || value.trim() === '') {
          return of(null);
        }

        return promptModuleApiService
          .validate({
            content: value,
          })
          .pipe(
            map((response) => {
              if (response.isValid) {
                return null;
              } else {
                return {
                  templateSyntax: true,
                };
              }
            }),
            catchError(() => {
              return of(null);
            }),
          );
      }),
      take(1),
    );
  };
}

/**
 * Cross-field validator that requires url and method fields when generatesAnswer is false
 */
export function requireUrlAndMethodWhenNotGeneratingAnswer(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    if (!formGroup || typeof formGroup.get !== 'function') {
      return null;
    }

    const generatesAnswer = formGroup.get('generatesAnswer')?.value;
    const noOp = formGroup.get('noOp')?.value;
    const url = formGroup.get('url');
    const method = formGroup.get('method');

    // If generatesAnswer is true, no validation needed for url/method
    if (generatesAnswer || noOp) {
      return null;
    }

    const errors: ValidationErrors = {};

    // Check if URL is required and missing
    if (!url?.value) {
      errors['urlRequired'] = true;
    } else {
      // Also validate URL format if present
      const urlValidationResult = urlValidator(url);
      if (urlValidationResult) {
        errors['urlInvalid'] = true;
      }
    }

    // Check if method is required and missing
    if (!method?.value) {
      errors['methodRequired'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}
